import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Pause,
  XCircle,
  Edit,
  Trash2,
  AlertCircle,
  Loader2,
  RefreshCw,
  TestTube2,
  Home,
  Play,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { format } from 'date-fns';
import { ValidationStepper } from './components';
import { Breadcrumb } from '../../components/ui';

interface Execution {
  id: string;
  model: string;
  iteration: number;
  blindCode: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

type RunType = 'MODEL_COMPARISON' | 'MODEL_STABILITY';

interface TestRun {
  id: string;
  name: string;
  description?: string;
  status: string;
  runType: RunType;
  isRevealed: boolean;
  totalExecutions: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  searchPhaseStatus?: 'NOT_STARTED' | 'READY' | 'RUNNING' | 'COMPLETED' | 'EVALUATED';
  config: {
    models: string[];
    iterations: number;
  };
  testCase: {
    id: string;
    name: string;
    code: string;
    category: string;
    complexity: string;
    condition: string;
  };
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  executions: Execution[];
  evaluationSets: Array<{
    id: string;
    completedAt?: string;
    _count: { items: number };
  }>;
  // Quality assessment sets for Phase 5
  qualityAssessmentSets?: Array<{
    id: string;
    completedAt?: string;
  }>;
}


const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  DRAFT: { label: 'Draft', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  PENDING: { label: 'Pending', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  RUNNING: { label: 'Running', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  PAUSED: { label: 'Paused', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  COMPLETED: { label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-100' },
  FAILED: { label: 'Failed', color: 'text-red-700', bgColor: 'bg-red-100' },
  CANCELLED: { label: 'Cancelled', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || { label: status, color: 'text-gray-700', bgColor: 'bg-gray-100' };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.bgColor, config.color)}>
      {config.label}
    </span>
  );
}

export function TestRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: testRunResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['validation', 'test-run', id],
    queryFn: () => api.get<{ data: TestRun }>(`/api/validation/test-runs/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const testRun = query.state.data?.data;
      // Poll when either Phase 1 or Phase 2 is running
      if (testRun?.status === 'RUNNING' || testRun?.searchPhaseStatus === 'RUNNING') {
        return 2000;
      }
      return false;
    },
  });

  const testRun = testRunResponse?.data;

  const pauseMutation = useMutation({
    mutationFn: () => api.post(`/api/validation/test-runs/${id}/pause`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => api.post(`/api/validation/test-runs/${id}/resume`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/api/validation/test-runs/${id}/cancel`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/validation/test-runs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
      navigate('/validation/test-runs');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error || !testRun) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Test Run Not Found</h2>
        <Link
          to="/validation/test-runs"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Test Runs
        </Link>
      </div>
    );
  }

  const totalExecutions = testRun.executions?.length || testRun.totalExecutions || 0;
  const completedEvaluations = (testRun.evaluationSets || []).filter((e) => e.completedAt).length;

  const canPause = testRun.status === 'RUNNING';
  const canResume = testRun.status === 'PAUSED';
  const canCancel = ['DRAFT', 'PENDING', 'RUNNING', 'PAUSED'].includes(testRun.status);
  const canEdit = testRun.status === 'DRAFT';
  const canDelete = testRun.status !== 'RUNNING';

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Model Validation', href: '/validation', icon: Home },
    { label: 'Test Runs', href: '/validation/test-runs', icon: Play },
    { label: testRun.name },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{testRun.name}</h1>
            <StatusBadge status={testRun.status} />
            <span className={clsx(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              testRun.runType === 'MODEL_STABILITY'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-blue-100 text-blue-700'
            )}>
              {testRun.runType === 'MODEL_STABILITY' ? 'Stability Test' : 'Model Comparison'}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Created {format(new Date(testRun.createdAt), 'PPp')} by {testRun.createdBy.name}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {canPause && (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {pauseMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
              Pause
            </button>
          )}
          {canResume && (
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              {resumeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Resume
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {cancelMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Cancel
            </button>
          )}
          {canEdit && (
            <Link
              to={`/validation/test-runs/${id}/edit`}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Link>
          )}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
          {testRun.status === 'RUNNING' && (
            <button onClick={() => refetch()} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete Test Run?</h3>
            <p className="mt-2 text-sm text-gray-500">
              This action cannot be undone. The following will be permanently deleted:
            </p>
            <ul className="mt-3 text-sm text-gray-600 space-y-1.5">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                Test run: <span className="font-medium">{testRun.name}</span>
              </li>
              {totalExecutions > 0 && (
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                  {totalExecutions} execution{totalExecutions !== 1 ? 's' : ''} and their results
                </li>
              )}
              {completedEvaluations > 0 && (
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                  {completedEvaluations} evaluation{completedEvaluations !== 1 ? 's' : ''}
                </li>
              )}
            </ul>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate();
                  setShowDeleteConfirm(false);
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {(cancelMutation.isError || deleteMutation.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Action Failed</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {(cancelMutation.error || deleteMutation.error) instanceof Error
              ? (cancelMutation.error || deleteMutation.error)?.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}

      {/* Test Case Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-100 p-2">
            <TestTube2 className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{testRun.testCase.name}</span>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">
                {testRun.testCase.code}
              </span>
            </div>
            <p className="text-sm text-gray-500 truncate">{testRun.testCase.condition}</p>
          </div>
          <Link
            to={`/validation/test-cases/${testRun.testCase.id}`}
            className="text-sm text-amber-600 hover:text-amber-700"
          >
            View Test Case
          </Link>
        </div>
      </div>

      {/* Unified Validation Stepper */}
      <ValidationStepper testRunId={id!} testRun={testRun} onRefetch={refetch} />
    </div>
  );
}
